/**
 * Class to help to communicate through MessageChannel
 */
export class MessageService {
  private port: MessagePort | null;
  private parentDomain: string;

  constructor(port: MessagePort | null, parentDomain: string) {
    this.port = port;
    this.parentDomain = parentDomain;
    Object.preventExtensions(this);
  }

  postMessage(msg: any): void {
    PostMessageUtil.postMessage(this.port, this.parentDomain, msg);
  }
}

/**
 * Class to help to communicate through MessageChannel
 */
export class PostMessageUtil {
  /**
   * @desc Wrapper around postMessage so that a message is always sent
   */
  static postMessage(
    port: MessagePort | null,
    parentDomain: string,
    message: any,
  ): void {
    try {
      if (!!port) {
        // Port is setup for Channel Messaging, use it first
        port.postMessage(message);
      } else if (window.parent && window.parent.postMessage && !!parentDomain) {
        // Parent domain is defined, so the frame can target it in the parent window
        window.parent.postMessage(message, parentDomain);
      }
    } catch (err) {}
  }
}

export class RequestUtil {
  // Example POST method implementation:
  static async postData(
    url: string = "",
    data: any = {},
    xIssuerId: string,
    xUserId: string,
    token: string,
  ): Promise<any> {
    // Default options are marked with *
    const response = await fetch(url, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json;charset=UTF-8",
        "X-Issuer-Id": xIssuerId,
        "X-User-Id": xUserId,
        Authorization: "Bearer " + token,
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });
    return response.json(); // parses JSON response into native JavaScript objects
  }

  static async putData(
    url: string = "",
    data: any = {},
    xIssuerId: string,
    xUserId: string,
    token?: string,
    rejectOnHttpError: boolean = false,
  ): Promise<any> {
    const initialHeadaers = {
      "Content-Type": "application/json",
      "X-Issuer-Id": xIssuerId,
      "X-User-Id": xUserId,
      "X-Request-Id": crypto.randomUUID(),
      // 'Content-Type': 'application/x-www-form-urlencoded',
    };
    // Default options are marked with *
    const response = await fetch(url, {
      method: "PUT", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      credentials: "omit",
      headers: token
        ? { ...initialHeadaers, Authorization: "Bearer " + token }
        : initialHeadaers,
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "strict-origin", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: JSON.stringify(data), // body data type must match "Content-Type" header
    });

    // Parse JSON when available to keep existing response behavior.
    const contentType = response.headers.get("content-type") || "";
    const responseBody = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (rejectOnHttpError && !response.ok) {
      return Promise.reject({
        status: response.status,
        statusText: response.statusText,
        body: responseBody,
      });
    }

    return responseBody;
  }

  static async getData(
    url: string = "",
    xIssuerId: string,
    xUserId: string,
    token: string,
  ): Promise<any> {
    // Default options are marked with *
    const response = await fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      credentials: "omit",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Accept-Language": "en-US",
        "Content-Type": "application/json",
        "X-Issuer-Id": xIssuerId,
        "X-User-Id": xUserId,
        Authorization: "Bearer " + token,
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      //referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      //body: JSON.stringify(data) // body data type must match "Content-Type" header
    });
    return response.json(); // parses JSON response into native JavaScript objects
  }

  static async getDataHTML(
    url: string = "",
    xIssuerId: string,
    xUserId: string,
    token: string,
  ): Promise<Response> {
    // Default options are marked with *
    const response = await fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      credentials: "omit",
      headers: {
        Accept: "application/json, text/html, */*",
        "Accept-Language": "en-US",
        "Content-Type": "application/json",
        "X-Issuer-Id": xIssuerId,
        "X-User-Id": xUserId,
        Authorization: "Bearer " + token,
      },
      redirect: "follow",
      referrerPolicy: "origin-when-cross-origin",
    });
    return response; // parses JSON response into native JavaScript objects
  }

  static async getDataFile(
    url: string = "",
    xIssuerId?: string,
    xUserId?: string,
    token?: string,
  ): Promise<Response> {
    // Default options are marked with *
    const response = await fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      credentials: "omit",
      redirect: "follow",
      referrerPolicy: "origin-when-cross-origin",
    });
    return response; // parses JSON response into native JavaScript objects
  }

  /**
   * Get the URL for a resource relative to a script location
   * @param scriptName Name of the script (e.g., 'web-push-provisioning.js')
   * @param relativePath Relative path from the script location (e.g., 'certs/key.pub' or 'config/config.json')
   * @param dataAttribute Optional data attribute to identify the script (e.g., 'data-wpp')
   * @returns The full URL to the resource
   * @throws Error if the script is not found
   */
  static getResourceUrl(
    scriptName: string,
    relativePath: string,
    dataAttribute?: string,
  ): string {
    // Find the specified script
    const scripts = Array.from(document.getElementsByTagName("script"));
    const targetScript = scripts.find(
      (script) =>
        script.src.includes(scriptName) ||
        (dataAttribute && script.getAttribute(dataAttribute) === "true"),
    );

    if (!targetScript || !targetScript.src) {
      throw new Error(`Script ${scriptName} not found`);
    }

    // Build the resource URL from the script location
    const scriptUrl = new URL(targetScript.src);
    const resourcePath = scriptUrl.pathname.replace(
      /js\/[^/]+\.js$/,
      relativePath,
    );
    return `${scriptUrl.origin}${resourcePath}`;
  }

  /**
   * Get a resource relative to a script location
   * @param scriptName Name of the script (e.g., 'web-push-provisioning.js')
   * @param relativePath Relative path from the script location (e.g., 'certs/key.pub' or 'config/config.json')
   * @param dataAttribute Optional data attribute to identify the script (e.g., 'data-wpp')
   * @returns Promise<Response> The fetch response
   * @throws Error if the script is not found
   */
  static async getResource(
    scriptName: string,
    relativePath: string,
    dataAttribute?: string,
  ): Promise<Response> {
    const resourceLocation = this.getResourceUrl(
      scriptName,
      relativePath,
      dataAttribute,
    );
    return this.getDataFile(resourceLocation);
  }
}

export class CustomDomUtil {
  static loadCss(cssUrl: string): void {
    const head = document.head || document.getElementsByTagName("head")[0],
      link = document.createElement("link");
    head.appendChild(link);
    link.setAttribute("rel", "stylesheet");
    link.setAttribute("type", "text/css");
    link.setAttribute("href", cssUrl);
  }

  static createDivElement(id: string): HTMLDivElement {
    const elt = document.createElement("div");
    elt.setAttribute("id", id);
    document.body.appendChild(elt);
    return elt;
  }

  static updateElementClass(id: string, css: string): void {
    document.getElementById(id)?.setAttribute("class", css);
  }
}

export class ConvertUtil {
  static hex2a(hexx: string): string {
    var hex = hexx.toString(); //force conversion
    var str = "";
    for (var i = 0; i < hex.length; i += 2)
      str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
    return str;
  }
}
